import styles from "./index.module.css";
import CalendarIcon from "../images/calendarIcon.svg?react";
import { PickersDay, pickersDayClasses, PickersDayProps } from "@mui/x-date-pickers";
import { buttonStyles } from "./shared";
import { forwardRef } from "react";
import { Button, useForkRef } from "@mui/material";
import { ScreenReaderText } from "../../screenReader";

export function DateOpenPickerIcon() {
	return (
		<div className={styles.openPickerIcon}>
			<CalendarIcon />
		</div>
	);
}

export const CustomDatePickersDay = ({ disabled, ...props }: PickersDayProps<Date>) => {
	return (
		<PickersDay
			{...props}
			disabled={props.outsideCurrentMonth || disabled}
			disableHighlightToday={false}
			sx={{
				[`&.${pickersDayClasses.root}`]: {
					fontSize: "12px",
					height: "26px",
					width: "26px",
					margin: "3px 5px",
					...buttonStyles,

					// override specific stylings in buttonStyles
					[`:hover`]: {
						backgroundColor: "#42296e",
						color: "white",
					},
				},
				[`&.${pickersDayClasses.dayOutsideMonth}`]: {
					opacity: 0.4,
				},
				[`&.${pickersDayClasses.today}`]: {
					border: "none",
					backgroundColor: "transparent",
				},
			}}
		/>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DatePickerButton = forwardRef<HTMLButtonElement, any>(function DatePickerButton(props, ref) {
	const {
		onClick,
		inputProps,
		InputProps,
		error,
		disabled,
		placeholder,
		onKeyDown: _onKeyDown,
		inputRef,
		value,
		sx,
		ownerState: _ownerState, // Destructure unused MUI props to prevent errors
		variant: _variant,
		color: _color,
		focused: _focused,
		label: _label,
		helperText: _helperText,
		fullWidth: _fullWidth,
		size: _size,
		hidePickerIcon,
		...other
	} = props;

	const {
		"aria-haspopup": ariaHasPopup,
		"aria-expanded": ariaExpanded,
		"aria-controls": ariaControls,
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		role: _role,
		inputRef: _inputPropsInputRef,
		...otherInputProps
	} = inputProps || {};

	const displayValue = value || inputProps?.value || placeholder || "MM/DD/YYYY";
	const handleRef = useForkRef(useForkRef(ref, inputRef), InputProps?.ref);
	const externalSx = Array.isArray(sx) ? Object.assign({}, ...sx) : sx;

	return (
		<Button
			{...other}
			{...otherInputProps}
			ref={handleRef}
			endIcon={hidePickerIcon ? undefined : <DateOpenPickerIcon />}
			onClick={onClick}
			disabled={disabled}
			aria-label={ariaLabel}
			aria-haspopup={ariaHasPopup}
			aria-expanded={ariaExpanded}
			aria-controls={ariaControls}
			aria-labelledby={ariaLabelledBy}
			style={{
				justifyContent: "space-between",
				paddingRight: "4px",
				fontSize: "14px",
				letterSpacing: "-0.05px",
				fontFeatureSettings: '"tnum" on, "lnum" on',
				textTransform: "none",
				border: hidePickerIcon ? "none" : `1px solid ${error ? "#bf0033" : "#6f6f6f"}`,
				color: "#4d4d4d",
			}}
			sx={{
				width: "185px",
				height: "36px",
				backgroundColor: "#ffffff",
				"&&&:hover, &&&:active, &&&:focus-visible": {
					backgroundColor: "rgba(0, 0, 0, 0.04)",
					borderColor: hidePickerIcon ? "transparent" : error ? "#bf0033" : "#6f6f6f",
					color: "#4d4d4d",
				},
				...externalSx,
			}}
		>
			{!ariaLabelledBy && ariaLabel && <ScreenReaderText>{ariaLabel}&nbsp;</ScreenReaderText>}
			{displayValue}
		</Button>
	);
});
